interface LogoProps {
  variant?: "wordmark" | "mark" | "stacked";
  className?: string;
  /** Tailwind height class, e.g. "h-5", "h-7". Width auto-scales via viewBox. */
  size?: string;
}

/**
 * Decyzon brand logo — Bracket + Dot system.
 * - `mark`: square symbol only (app icon, favicon, compact)
 * - `wordmark`: symbol left + DECYZON right (navbar, default)
 * - `stacked`: symbol on top, DECYZON below (splash, email)
 *
 * Brackets and wordmark inherit `currentColor`. The decision dot is always
 * brand blue (`hsl(var(--primary))`).
 */
export default function Logo({ variant = "wordmark", className = "", size = "h-5" }: LogoProps) {
  if (variant === "mark") {
    return (
      <svg
        viewBox="0 0 64 64"
        xmlns="http://www.w3.org/2000/svg"
        className={`${size} w-auto ${className}`}
        role="img"
        aria-label="Decyzon"
      >
        <path
          d="M22 17 H13 V47 H22"
          stroke="currentColor"
          strokeWidth={5}
          strokeLinecap="square"
          fill="none"
        />
        <path
          d="M42 17 H51 V47 H42"
          stroke="currentColor"
          strokeWidth={5}
          strokeLinecap="square"
          fill="none"
        />
        <circle cx={32} cy={32} r={6} className="fill-primary" />
      </svg>
    );
  }

  if (variant === "stacked") {
    return (
      <svg
        viewBox="0 0 240 120"
        xmlns="http://www.w3.org/2000/svg"
        className={`${size} w-auto ${className}`}
        role="img"
        aria-label="Decyzon"
      >
        <g transform="translate(88,8)">
          <path d="M22 17 H13 V47 H22" stroke="currentColor" strokeWidth={5} strokeLinecap="square" fill="none" />
          <path d="M42 17 H51 V47 H42" stroke="currentColor" strokeWidth={5} strokeLinecap="square" fill="none" />
          <circle cx={32} cy={32} r={6} className="fill-primary" />
        </g>
        <text
          x={120}
          y={104}
          textAnchor="middle"
          fontFamily="'Space Grotesk', 'Inter', system-ui, sans-serif"
          fontWeight={700}
          fontSize={28}
          letterSpacing={1.5}
          fill="currentColor"
        >
          DECYZON
        </text>
      </svg>
    );
  }

  // wordmark (symbol + text, horizontal). Tightened gap + heavier stroke + bolder text.
  return (
    <svg
      viewBox="0 0 320 64"
      xmlns="http://www.w3.org/2000/svg"
      className={`${size} w-auto ${className}`}
      role="img"
      aria-label="Decyzon"
    >
      <g transform="translate(0,12)">
        <path d="M14 4 H7 V36 H14" stroke="currentColor" strokeWidth={3.6} strokeLinecap="square" fill="none" />
        <path d="M30 4 H37 V36 H30" stroke="currentColor" strokeWidth={3.6} strokeLinecap="square" fill="none" />
        <circle cx={22} cy={20} r={4.2} className="fill-primary" />
      </g>
      <text
        x={50}
        y={44}
        fontFamily="'Space Grotesk', 'Inter', system-ui, sans-serif"
        fontWeight={700}
        fontSize={34}
        letterSpacing={0.8}
        fill="currentColor"
      >
        DECYZON
      </text>
    </svg>
  );
}
